import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import {
  DEFAULT_NODE_WIDTH,
  ZAP_STEP_NODE_HEIGHT,
} from "@zapier/canvas-constants";
import { Node } from "@xyflow/react";

import { ZapMetadata, ZapNodeData } from "../types";
import { findPathParentForVisibleSteps } from "../utils";
import { nodesAtom } from "../nodes";

import {
  ZapErrorObject,
  zapMetadataAtomFamily_SUSPENDS,
} from "./zapAtomFamily_SUSPENDS";

export const zapStepsByIdAtomFamily_SUSPENDS = atomFamily(
  (zapNodeId: string) => {
    const zapStepsAtom = atom(async (get) => {
      const nodes = get(nodesAtom);
      const zapNode = nodes.find(
        (node) => node.id === zapNodeId,
      ) as Node<ZapNodeData>;
      const zapId = zapNode?.data.zapId;

      const fetchedZap = await get(zapMetadataAtomFamily_SUSPENDS(zapId));
      const isPublished = (fetchedZap as ZapMetadata)?.is_published ?? false;
      const zap =
        !fetchedZap || (fetchedZap as ZapErrorObject).error
          ? undefined
          : (fetchedZap as ZapMetadata);

      const zapSteps = zap?.steps ?? [];
      const visibleStepsWithPathParents =
        findPathParentForVisibleSteps(zapSteps);

      const zapStepNodes = visibleStepsWithPathParents.map((step, i) => {
        return {
          type: "zapStep",
          id: `zap-${zapNode.id}-${step.id}`,
          data: {
            stepNumber: i + 1,
            step,
            isZapPublished: isPublished,
          },
          position: {
            x: 0,
            y: 0,
          },
          measured: {
            width: DEFAULT_NODE_WIDTH,
            height: ZAP_STEP_NODE_HEIGHT,
          },
          parentId: zapNode.id,
          selectable: false, // we don't want multi-select panel to count zap steps, just the zap node
          deletable: false,
          draggable: false,
          connectable: isPublished,
        };
      });

      const zapStepEdges = zapStepNodes.map((node, i) => {
        const pathParentId = node.data.step.pathParentId;
        const pathParentFullId = `zap-${zapNode.id}-${pathParentId}`;

        const source =
          i === 0
            ? node.parentId
            : pathParentId
              ? pathParentFullId
              : zapStepNodes[i - 1].id;
        const target = node.id;
        const sourceHandle =
          i === 0 ? "zap-node-bottom-handle" : "zap-step-bottom-handle";
        const targetHandle = "zap-step-top-handle";

        return {
          id: `${source}=>${target}`,
          type: "zapStepEdge",
          source,
          target,
          sourceHandle,
          targetHandle,
          deletable: false,
          focusable: false,
          selectable: false,
          reconnectable: false,
          animated: true,
        };
      });

      const zapStepsId = zapStepNodes.map((zapStep) => zapStep.id);

      const zapStepEdgesId = zapStepEdges.map((edge) => edge.id);

      return {
        nodes: zapStepNodes,
        edges: zapStepEdges,
        nodeIDs: zapStepsId,
        edgeIDs: zapStepEdgesId,
      };
    });

    zapStepsAtom.debugLabel = `zapStepsByIdAtomFamily_SUSPENDS(${zapNodeId})`;
    return zapStepsAtom;
  },
);
